import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import { roleBadgeClass } from '../../constants/roles';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // A fejléc csak görgetés után kap keretet/hátteret — így a hero tetején
  // beleolvad az oldalba, lejjebb viszont olvasható marad.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Útvonalváltáskor a mobil menü magától záródjon.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const navLink = (to, label) => (
    <Link
      key={to}
      to={to}
      className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-smooth ${
        isActive(to)
          ? 'text-white bg-white/[0.06]'
          : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
      }`}
    >
      {label}
      {isActive(to) && (
        <span className="absolute inset-x-3 -bottom-px h-px bg-gradient-to-r from-transparent via-brand-400 to-transparent" />
      )}
    </Link>
  );

  const mobileLink = (to, label) => (
    <Link
      key={to}
      to={to}
      onClick={() => setMobileOpen(false)}
      className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isActive(to) ? 'text-white bg-white/[0.07]' : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
      }`}
    >
      {label}
    </Link>
  );

  const links = [
    ['/', 'Home'],
    ...(user ? [['/panel', 'Dashboard']] : []),
    ['/docs', 'Docs'],
    ...((user?.role === 'admin' || user?.role === 'owner') ? [['/admin', 'Admin']] : []),
    ...(user?.role === 'owner' ? [['/owner', 'Owner']] : []),
  ];

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ease-smooth ${
        scrolled || mobileOpen
          ? 'bg-gray-950/80 backdrop-blur-xl border-b border-white/[0.07] shadow-card'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logó */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <span className="relative">
              <span className="absolute -inset-1.5 rounded-full bg-brand-500/25 blur-lg opacity-0
                               group-hover:opacity-100 transition-opacity duration-300" />
              <img
                src="/newlogo.png"
                alt=""
                className="relative w-9 h-9 object-contain drop-shadow-[0_0_10px_rgba(143,82,201,0.35)]"
              />
            </span>
            <span className="font-display font-bold text-white text-[17px] tracking-tight">
              Mortis<span className="text-brand-300">Engine</span>
            </span>
          </Link>

          {/* Desktop navigáció */}
          <div className="hidden md:flex items-center gap-1">
            {links.map(([to, label]) => navLink(to, label))}
          </div>

          {/* Jobb oldal */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/panel"
                  className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full
                             bg-white/[0.04] border border-white/[0.08]
                             hover:bg-white/[0.07] hover:border-white/[0.14]
                             transition-all duration-200 ease-smooth"
                >
                  <span className="w-7 h-7 rounded-full bg-brand-gradient border border-brand-400/30
                                   flex items-center justify-center text-[11px] font-bold text-white uppercase">
                    {user.username?.[0] || '?'}
                  </span>
                  <span className="text-sm text-gray-200 max-w-[9rem] truncate">{user.username}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5
                                    rounded border ${roleBadgeClass(user.role)}`}>
                    {user.role}
                  </span>
                </Link>
                <button onClick={handleLogout} className="btn-ghost text-sm py-2" title="Sign out">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost text-sm py-2">Login</Link>
                <Link to="/register" className="btn-primary text-sm py-2">Register</Link>
              </>
            )}
          </div>

          {/* Mobil hamburger */}
          <button
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="md:hidden btn-icon"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              {mobileOpen
                ? <path d="M6 18L18 6M6 6l12 12" />
                : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>

        {/* Mobil menü */}
        {mobileOpen && (
          <div className="md:hidden pb-4 pt-1 flex flex-col gap-1 animate-fade-in">
            <div className="hairline mb-2" />
            {links.map(([to, label]) => mobileLink(to, label))}

            <div className="mt-3 pt-3 border-t border-white/[0.06] flex flex-col gap-2">
              {user ? (
                <>
                  <div className="flex items-center gap-2.5 px-1 pb-1">
                    <span className="w-8 h-8 rounded-full bg-brand-gradient border border-brand-400/30
                                     flex items-center justify-center text-xs font-bold text-white uppercase">
                      {user.username?.[0] || '?'}
                    </span>
                    <span className="text-sm text-gray-200 truncate">{user.username}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5
                                      rounded border ${roleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </div>
                  <button onClick={handleLogout} className="btn-secondary w-full">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-secondary w-full">Login</Link>
                  <Link to="/register" className="btn-primary w-full">Register</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
